import type {
  FetchCampaignCreatorCountsRequest,
  FetchCampaignCreatorsRequest,
  FetchCampaignPostsRequest,
} from '@/entities/campaign';
import type { CampaignListRequest } from '@/entities/campaign/api/fetch-campaign-list';
import type {
  ListCreatorDiscoverySearchResultsRequest,
  ListDiscoveryMessagesRequest,
} from '@/entities/discovery/api';

type InfiniteCampaignListRequest = Omit<CampaignListRequest, 'page'>;

export function createQueryKeys(workspaceId: string) {
  const createBoardPostsKey = (boardId: string) =>
    [workspaceId, 'board', 'posts', boardId] as const;
  const createCampaignListsKey = (params?: CampaignListRequest) =>
    params == null
      ? ([workspaceId, 'campaign', 'lists'] as const)
      : ([
          workspaceId,
          'campaign',
          'lists',
          params.sort ?? null,
          params.status ?? null,
          params.limit,
          params.page,
        ] as const);
  const createInfiniteCampaignListsKey = (params?: InfiniteCampaignListRequest) =>
    params == null
      ? ([workspaceId, 'campaign', 'lists', 'infinite'] as const)
      : ([
          workspaceId,
          'campaign',
          'lists',
          'infinite',
          params.name ?? null,
          params.sort ?? null,
          params.status ?? null,
          params.limit,
        ] as const);

  return {
    all: [workspaceId] as const,
    workspace: {
      detail: () => [workspaceId, 'workspace'] as const,
    },

    savedCreatorsList: {
      all: () => [workspaceId, 'saved-creators-list'] as const,
      lists: () => [workspaceId, 'saved-creators-list', 'lists'] as const,
      list: () => [workspaceId, 'saved-creators-list', 'list'] as const,
      detail: (listId: string) => [workspaceId, 'saved-creators-list', 'detail', listId] as const,
      handles: (listId: string) => [workspaceId, 'saved-creators-list', 'handles', listId] as const,
      outreachStatuses: (listId: string) =>
        [workspaceId, 'saved-creators-list', 'outreach-statuses', listId] as const,
      campaignDraftCreators: (listId: string, creatorIds: string[]) =>
        [
          workspaceId,
          'saved-creators-list',
          'campaign-draft-creators',
          listId,
          creatorIds.join(','),
        ] as const,
    },
    board: {
      all: () => [workspaceId, 'board'] as const,
      lists: () => [workspaceId, 'board', 'lists'] as const,
      detail: (boardId: string) => [workspaceId, 'board', 'detail', boardId] as const,
      postsRoot: (boardId: string) => createBoardPostsKey(boardId),
      posts: (boardId: string, params?: { page?: number; limit?: number } | null) =>
        [
          ...createBoardPostsKey(boardId),
          'page',
          params?.page ?? 0,
          'limit',
          params?.limit ?? 20,
        ] as const,
      infinitePosts: (boardId: string, limit: number) =>
        [...createBoardPostsKey(boardId), 'infinite', 'limit', limit] as const,
    },

    campaign: {
      all: () => [workspaceId, 'campaign'] as const,
      lists: (params: CampaignListRequest) => createCampaignListsKey(params),
      infiniteLists: (params?: InfiniteCampaignListRequest) =>
        createInfiniteCampaignListsKey(params),
      slot: () => ['campaign', 'slot'] as const,
      nameAvailability: (name: string) =>
        [workspaceId, 'campaign', 'name-availability', name] as const,
      detail: (campaignId: string) => [workspaceId, 'campaign', 'detail', campaignId] as const,
      overview: (campaignId: string) => [workspaceId, 'campaign', 'overview', campaignId] as const,
      metrics: (campaignId: string) => [workspaceId, 'campaign', 'metrics', campaignId] as const,
      creatorsRoot: (campaignId: string) =>
        [workspaceId, 'campaign', 'creators', campaignId] as const,
      creators: (campaignId: string, pending: boolean, request?: FetchCampaignCreatorsRequest) =>
        [workspaceId, 'campaign', 'creators', campaignId, pending, request] as const,
      creatorCountsRoot: (campaignId: string) =>
        [workspaceId, 'campaign', 'creator-counts', campaignId] as const,
      creatorCount: (
        campaignId: string,
        pending: boolean,
        request?: FetchCampaignCreatorCountsRequest,
      ) => [workspaceId, 'campaign', 'creator-counts', campaignId, pending, request] as const,
      postsRoot: (campaignId: string) => [workspaceId, 'campaign', 'posts', campaignId] as const,
      posts: (campaignId: string, recent: boolean, request?: FetchCampaignPostsRequest) =>
        [
          workspaceId,
          'campaign',
          'posts',
          campaignId,
          recent,
          'sort',
          request?.sort ?? null,
          'page',
          request?.page ?? 1,
          'limit',
          request?.limit ?? null,
        ] as const,
      postCountsRoot: (campaignId: string) =>
        [workspaceId, 'campaign', 'post-counts', campaignId] as const,
      postCount: (campaignId: string, recent: boolean) =>
        [workspaceId, 'campaign', 'post-counts', campaignId, recent] as const,
    },

    excludeList: {
      all: () => [workspaceId, 'exclude-list'] as const,
    },

    creator: {
      all: () => [workspaceId, 'creator'] as const,
      searchStatus: (searchId: string) =>
        [workspaceId, 'creator', 'search-status', searchId] as const,
    },

    discovery: {
      all: () => [workspaceId, 'discovery'] as const,
      currentSession: () => [workspaceId, 'discovery', 'current-session'] as const,
      messagesRoot: (sessionId: string) =>
        [workspaceId, 'discovery', 'messages', sessionId] as const,
      messages: (sessionId: string, request?: ListDiscoveryMessagesRequest) =>
        request == null
          ? ([workspaceId, 'discovery', 'messages', sessionId] as const)
          : ([
              workspaceId,
              'discovery',
              'messages',
              sessionId,
              'limit',
              request.limit ?? null,
              'cursor',
              request.cursor ?? null,
            ] as const),
      searches: () => [workspaceId, 'discovery', 'search'] as const,
      searchStatus: (sessionId: string, searchId: string) =>
        [workspaceId, 'discovery', 'search', sessionId, searchId, 'status'] as const,
      searchResults: (
        sessionId: string,
        searchId: string,
        request: Omit<ListCreatorDiscoverySearchResultsRequest, 'attempt'>,
      ) =>
        [
          workspaceId,
          'discovery',
          'search',
          sessionId,
          searchId,
          'results',
          'show_saved',
          request.show_saved ?? null,
        ] as const,
    },
  };
}

export type QueryKeys = ReturnType<typeof createQueryKeys>;
